// region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Method to validate the application data.
//
//  Copyright:    © 2026 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
// region Version 1.0.0.0
//    001   16.01.26 Sean Flook         GMSCM-12 Initial Revision.
// endregion Version 1.0.0.0
//
//--------------------------------------------------------------------------------------------------
// endregion header

import { ApplicationNamesDataDto } from "../dtos/applicationNamesDto";
import { ApplicationErrorType } from "../models/applicationErrorType";

export const validateApplicationData = (data: ApplicationNamesDataDto[]): ApplicationErrorType[] => {
  let applicationErrors: ApplicationErrorType[] = [];

  if (data && data.length > 0) {
    data.forEach((application) => {
      const dbConnectionNames = application.dbConnectionNames ?? [];
      const elasticConnectionNames = application.elasticConnectionNames ?? [];

      // Validate that the application name is not empty.
      if (!application.name || application.name.trim() === "") {
        applicationErrors.push({
          id: application.id,
          field: "name",
          error: "Application name cannot be empty.",
        });
      }

      // Validate that the application name does not equal "_New Application".
      if (application.name === "_New Application") {
        applicationErrors.push({
          id: application.id,
          field: "name",
          error: "Application name cannot be '_New Application'. Please choose a different name.",
        });
      }

      // Validate that the application name is unique.
      const duplicateApps = data.filter((app) => app.name === application.name);
      if (duplicateApps.length > 1) {
        applicationErrors.push({
          id: application.id,
          field: "name",
          error: `Application name '${application.name}' must be unique.`,
        });
      }

      // Validate that there is at least one DB connection.
      if (dbConnectionNames.length === 0) {
        applicationErrors.push({
          id: application.id,
          field: "dbConnectionNames",
          error: `Application '${application.name}' must have at least one connection setting name.`,
        });
      }

      // Validate that there is at least one Elastic connection.
      if (elasticConnectionNames.length === 0) {
        applicationErrors.push({
          id: application.id,
          field: "elasticConnectionNames",
          error: `Application '${application.name}' must have at least one elastic setting name.`,
        });
      }

      // Validate that DB connection names are unique within the application.
      const dbNameSet = new Set<string>();
      dbConnectionNames.forEach((dbName) => {
        if (dbNameSet.has(dbName)) {
          applicationErrors.push({
            id: application.id,
            field: `dbConnectionNames|${dbName}`,
            error: `Connection setting name '${dbName}' must be unique within application '${application.name}'.`,
          });
        } else {
          dbNameSet.add(dbName);
        }
      });

      // Validate that DB connection names do not have spaces
      dbConnectionNames.forEach((dbName) => {
        if (/\s/.test(dbName)) {
          applicationErrors.push({
            id: application.id,
            field: `dbConnectionNames|${dbName}`,
            error: `Connection setting name '${dbName}' cannot contain spaces in application '${application.name}'.`,
          });
        }
      });

      // Validate that Elastic connection names are unique within the application.
      const elasticNameSet = new Set<string>();
      elasticConnectionNames.forEach((elasticName) => {
        if (elasticNameSet.has(elasticName)) {
          applicationErrors.push({
            id: application.id,
            field: `elasticConnectionNames|${elasticName}`,
            error: `Elastic setting name '${elasticName}' must be unique within application '${application.name}'.`,
          });
        } else {
          elasticNameSet.add(elasticName);
        }
      });

      // Validate that Elastic connection names do not have spaces
      elasticConnectionNames.forEach((elasticName) => {
        if (/\s/.test(elasticName)) {
          applicationErrors.push({
            id: application.id,
            field: `elasticConnectionNames|${elasticName}`,
            error: `Elastic setting name '${elasticName}' cannot contain spaces in application '${application.name}'.`,
          });
        }
      });
    });
  }

  return applicationErrors;
};
